import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { PropiedadDto } from '@models/propiedades/propiedadDto.model';
import { PropiedadesService } from '@services/propiedades/propiedades.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { isFalsy } from 'utility-types';

/**
 * Vista de sólo lectura de una propiedad.
 *
 * Va separada de la edición a propósito: la mayoría de las veces se entra a
 * mirar, no a modificar. Con un formulario editable de por medio se corre el
 * riesgo de tocar un campo sin querer, y además obliga a cargar los combos de
 * tipos, clases y folios sólo para mostrar una descripción que ya viene en el
 * DTO.
 *
 * Acá viven los tabs de Propietarios y Características; la edición se queda
 * únicamente con los datos de la propiedad.
 */
@Component({
  selector: 'app-ver-propiedad',
  templateUrl: './ver-propiedad.component.html',
  styleUrls: ['./ver-propiedad.component.css']
})
export class VerPropiedadComponent implements OnInit {

  idPropiedad: number = 0
  propiedadDto!: PropiedadDto
  cargando = true
  fallo = false

  constructor(
    private aRoute: ActivatedRoute,
    private router: Router,
    private propiedadesService: PropiedadesService,
    private spinner: NgxSpinnerService,
    private snackBar: SnackBarService,
  ) { }

  ngOnInit(): void {
    this.idPropiedad = Number(this.aRoute.snapshot.paramMap.get('id'))

    // Otros componentes de la pantalla (propietarios, características) leen el
    // id desde el servicio, igual que en la edición.
    this.propiedadesService.setIdPropiedad(this.idPropiedad)

    this.getPropiedadById(this.idPropiedad)
  }

  getPropiedadById(id: number) {
    this.cargando = true
    this.fallo = false
    this.spinner.show("spVerPropiedad")

    this.propiedadesService.getById(id)
      .subscribe(
        data => {
          this.spinner.hide("spVerPropiedad")
          this.propiedadDto = data
          this.cargando = false
        },
        error => {
          this.spinner.hide("spVerPropiedad")
          this.fallo = true
          this.cargando = false
          this.snackBar.showError(error, "Error");
        })
  }

  editar() {
    this.router.navigate(["/propiedades", this.idPropiedad])
  }

  volver() {
    this.router.navigate(["/propiedades"])
  }

  /**
   * Dirección completa en una línea.
   *
   * Se arma acá y no en el template para no repetir cuatro ternarios anidados
   * cada vez que hay que mostrarla.
   */
  get direccion(): string {
    if (isFalsy(this.propiedadDto))
      return ''

    const partes = [
      this.propiedadDto.ubicacion,
      this.propiedadDto.piso ? 'Piso ' + this.propiedadDto.piso : null,
      this.propiedadDto.departamento ? 'Depto ' + this.propiedadDto.departamento : null
    ]

    return partes.filter(f => !isFalsy(f) && f !== '').join(' · ')
  }

  get localidad(): string {
    if (isFalsy(this.propiedadDto) || isFalsy(this.propiedadDto.localidad))
      return '-'

    const localidad = this.propiedadDto.localidad
    const provincia = localidad.provincia ? localidad.provincia.descripcion : null

    return provincia ? localidad.descripcion + ' · ' + provincia : (localidad.descripcion || '-')
  }


  /** Los folios sólo tienen sentido cuando el tipo de folio los usa */
  get muestraDatosRegistrales(): boolean {
    const idTipoFolio = this.propiedadDto?.tipoFolio?.id

    return idTipoFolio === 1 || idTipoFolio === 2
  }

  isFalsy(valor: any) {
    return isFalsy(valor)
  }
}
